import { makeStyles, tokens } from '@fluentui/react-components'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatInput } from '../../components/ChatInput/ChatInput'
import { Conversation } from '../../components/Conversation'
import { Dropzone } from '../../components/Dropzone/Dropzone'
import { EmptyHomeState } from '../../components/EmptyHomeState/EmptyHomeState'
import { Header } from '../../components/Header/Header'
import { ReviewModeButton } from '../../components/ReviewModeButton/ReviewModeButton'
import { SettingsSidebar } from '../../components/SettingsSidebar/SettingsSidebar'
import { REVIEW_MODE_ENABLED } from '../../constants/chat'
import { WORKING_DOCUMENT_ID } from '../../constants/files'
import { useChatSession } from '../../hooks'
import { useMessageActions } from '../../hooks/useMessageActions'
import { useSelectedContext } from '../../hooks/useSelectedContext'
import { clearStoredPromptTab } from '../Prompts/prompt-tab-storage'
import type { ModelKey, SendContext } from '../../types'

const useStyles = makeStyles({
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  chatContainer: {
    display: 'flex',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  chatInputContainer: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `0 ${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    boxSizing: 'border-box',
    // Creates a stacking context so the ::before gradient ring in ChatInput
    // (z-index: -1) stays within this container's paint layer. Without this,
    // the ring falls to the root stacking context and is hidden behind the
    // page background (regression introduced when chatInputContainer moved
    // from position:fixed — which created a stacking context automatically —
    // to normal flex flow).
    isolation: 'isolate',
  },
  chatInputSlot: {
    width: '100%',
  },
  reviewSection: {
    flexShrink: 0,
  },
})

const Home = () => {
  const classes = useStyles()
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [editorContentSetter, setEditorContentSetter] = useState<
    ((content: string) => void) | null
  >(null)
  const [focusEditorFn, setFocusEditorFn] = useState<(() => void) | null>(null)
  const { t } = useTranslation()

  const {
    messages,
    isLoading,
    sendMessage,
    undoSend,
    resetChatSession,
    clearMessages,
    clearAgentSession,
    setSystemPrompt,
    setReviewMode,
    setSelectedModel,
    setIsCommentMode,
    setRequiresFullDocument,
  } = useChatSession()

  const { updateFileSelection, clearAll } = useSelectedContext()

  const handleNewChat = useCallback(() => {
    undoSend()
    resetChatSession()
    clearMessages()
    clearAll() // clears the file context selection
    clearStoredPromptTab()
    clearAgentSession()
  }, [undoSend, resetChatSession, clearMessages, clearAll, clearAgentSession])

  const handleSend = useCallback(
    (content: string, model: ModelKey) => {
      sendMessage(content, model, () => {
        editorContentSetter?.(content)
        setTimeout(() => focusEditorFn?.(), 0)
      })
    },
    [sendMessage, editorContentSetter, focusEditorFn]
  )

  const handleEditMessage = (content: string, sendContext?: SendContext) => {
    if (editorContentSetter) {
      editorContentSetter(content)
    }
    if (sendContext?.systemPrompt !== undefined) setSystemPrompt(sendContext.systemPrompt)
    if (sendContext?.reviewMode !== undefined) {
      setReviewMode(sendContext.reviewMode)
      if (REVIEW_MODE_ENABLED && sendContext.reviewMode) {
        setIsCommentMode(true)
      }
    } else if ((sendContext as { isQcPrompt?: boolean } | undefined)?.isQcPrompt) {
      setReviewMode('mouse_selection_comments')
      if (REVIEW_MODE_ENABLED) {
        setIsCommentMode(true)
      }
    }
    if (sendContext?.model !== undefined) setSelectedModel(sendContext.model)
    if (sendContext?.requiresFullDocument) {
      // Prompt required full document context when originally sent — restore it on re-use.
      updateFileSelection({
        id: WORKING_DOCUMENT_ID,
        fileName: t('files.workingDocumentFileName'),
        extension: 'docx',
        sections: [],
      })
      // Ensure it's re-enabled for the reuse of the new message
      setRequiresFullDocument(true)
    }
    // Defer focus so React re-renders and useEditorSync pushes content before cursor placement
    setTimeout(() => focusEditorFn?.(), 0)
  }

  const { handleCopy, handleInsert, handleEdit } = useMessageActions(messages, handleEditMessage)

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true)
  }, [])

  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false)
  }, [])

  const handleUndoSend = useCallback(() => {
    const content = undoSend()
    if (content && editorContentSetter) {
      editorContentSetter(content)
      // Defer focus so React re-renders and useEditorSync pushes content before cursor placement
      setTimeout(() => focusEditorFn?.(), 0)
    }
  }, [undoSend, editorContentSetter, focusEditorFn])

  const handleSetContent = useCallback(
    (setContent: (content: string) => void, focus: () => void) => {
      setEditorContentSetter(() => setContent)
      setFocusEditorFn(() => focus)
    },
    []
  )

  return (
    <Dropzone>
      <div className={classes.layout} data-home-layout>
        <Header onMenuClick={openSettings} onChatClear={handleNewChat} />
        <div className={classes.chatContainer}>
          {messages.length === 0 ? (
            <EmptyHomeState />
          ) : (
            <Conversation
              messages={messages}
              isLoading={isLoading}
              onCopy={handleCopy}
              onEdit={handleEdit}
              onInsert={handleInsert}
            />
          )}
        </div>
        <div className={classes.chatInputContainer} data-chat-footer>
          <div className={classes.chatInputSlot}>
            <ChatInput
              onSend={handleSend}
              onSetContent={handleSetContent}
              isLoading={isLoading}
              onUndoSend={handleUndoSend}
            />
          </div>
          {REVIEW_MODE_ENABLED && (
            <div className={classes.reviewSection} data-review-section>
              <ReviewModeButton />
            </div>
          )}
        </div>
        <SettingsSidebar isOpen={isSettingsOpen} onClose={closeSettings} />
      </div>
    </Dropzone>
  )
}

export { Home }
